import { gql } from 'apollo-angular';
import { TypedDocumentNode } from '@apollo/client';
import { paginationQuery } from '../../../queries/pagination.query';
import {
  addressGQL,
  attachments,
  propertyPsSide,
  rentDepositInfoFields,
  searchProfilesGQL
} from '../gql-queries';
import {
  ConversationMessageResponse,
  ConversationMessagesAndDetailsResponse,
  ConversationResponse,
  CreateTemplateResponse,
  LoadTemplateResponse,
  ParticipatedAgentsResponse,
  SendMessageInNewConversationResponse,
  SendMessageResponse,
  UpdateTemplateResponse
} from '../../../components/legacy/messenger/model/interface';

export const conversationFields = `
    id
    lastMessageDate
    lastMessageText
    unreadMessages
    subject
    titleImage
    mainConversationPartner {
      firstname
      name
      portrait
      dkApprovalLevel
      type
    }
    otherParticipants
    labels
`;

export const conversationMessageFields = `
    id
    conversationId
    messageSent
    message
    sender {
      firstname
      name
      portrait
      dkApprovalLevel
      type
    }
    source
    read
    attachments {
      title
      url
      encrypted
    }
`;

export const searchConversationQuery: TypedDocumentNode<ConversationResponse> = gql`
  query searchConversation($input: ConversationInput) {
    searchConversation(input: $input) {
      nodes {
        ${conversationFields}
      }
      page {
        ${paginationQuery}
      }
    }
  }
`;

export const searchConversationMessagesByConversationIdQuery: TypedDocumentNode<ConversationMessageResponse> = gql`
  query searchConversationMessagesByConversationId($input: ConversationMessageInput) {
    searchConversationMessagesByConversationId(input: $input) {
      nodes {
        ${conversationMessageFields}
      }
      page {
        ${paginationQuery}
      }
    }
  }
`;

export const conversationMessagesAndDetailsLLQuery: TypedDocumentNode<ConversationMessagesAndDetailsResponse> = gql`
  query conversationMessagesAndDetails($input: ConversationMessageInput) {
    searchConversationMessagesByConversationId(input: $input) {
      nodes {
        ${conversationMessageFields}
      }
      page {
        ${paginationQuery}
      }
    }

    conversationDetails(input: $input) {
      id
      participants {
        firstname
        name
        portrait
        dkApprovalLevel
        type
        email
        phone
        mobile
      }
      type
      blocked
      applicationDetails {
        isProspect
        id
        contactId
        created
        score
        scoreObscured
        customQuestionsAnswerOverview
        status
        attendingViewing
        attendingExclusiveViewing
        applicationBlocked
        askedForIntent
        dkApprovalLevel
        searchingSince
        entryDate
        updateStatus
        applicationConversationStatus
        systemTags
        customQuestionsAnswerOverview
        hasComments
        hasCreditReport
        hasIncomeDocument
        hasWbsDocument
        searchingSince
        rating
        text
        profileData {
          firstname
          name
          email
          membershipNumber
          phone
          dateOfBirth
          animals
          householdType
          residents
          furtherInformation
          guarantorExist
          preferredLanguage
          ${addressGQL}
          ${attachments}
          housingPermission {
            types
            amountPeople
            expiryDate
            amountRooms
            approvedObjectSize
          }
          profession {
            income
            subType
            type
          }
          moveInDate
          portrait {
            url
          }
        }
        salesLegalTexts {
          text
          type
        }
        tags {
          id
          title
          tagOwnershipType
          color
        }
        searchProfileBean {
          ${searchProfilesGQL}
        }
        waitingSince
        propertyId
      }
      ticketDetails {
        id
        title
        status
      }
      propertyDetailsLL
    }
  }
`;

export const conversationMessagesAndDetailsPSQuery: TypedDocumentNode<ConversationMessagesAndDetailsResponse> = gql`
  query conversationMessagesAndDetails($input: ConversationMessageInput) {
    searchConversationMessagesByConversationId(input: $input) {
      nodes {
        ${conversationMessageFields}
      }
      page {
        ${paginationQuery}
      }
    }

    conversationDetails(input: $input) {
      id
      participants {
        firstname
        name
        portrait
        type
      }
      type
      userAllowedToSendMessage
      customerIdent
      propertyDetailsPS {
        id
        type
        status
        customerIdent
        appointmentSlotsAvailable
        upcomingAppointmentDate
        upcomingAppointmentId
        hasQuestions
        hasUnansweredQuestions
        conversationExists
        allowedToSendMessage
        askForViewingAppointmentAttendance
        rentDepositInfo {
          ${rentDepositInfoFields}
        }
        ${propertyPsSide}
      }
    }
  }
`;

export const sendMessageMutation: TypedDocumentNode<SendMessageResponse> = gql`
  mutation sendMessageMutation($input: SendMessageInput!) {
    sendMessage(input: $input) {
      ${conversationMessageFields}
    }
  }
`;

export const sendMessageInNewConversationMutation: TypedDocumentNode<SendMessageInNewConversationResponse> = gql`
  mutation sendMessageInNewConversation($input: SendMessageInput!) {
    sendMessageInNewConversation(input: $input) {
      ${conversationMessageFields}
    }
  }
`;

export const bulkSendMessageMutation = gql`
  mutation bulkSendMessage($input: BulkSendMessageInput!) {
    bulkSendMessage(input: $input) {
      status
      statusText
    }
  }
`;

export const blockConversation = gql`
  mutation blockConversation($input: BlockInput!) {
    blockConversation(input: $input)
  }
`;

export const findUnreadMessages = gql`
  query findUnreadMessages($conversationId: ID!) {
    findUnreadMessages(conversationId: $conversationId) {
      nodes {
        ${conversationMessageFields}
      }
      page {
        ${paginationQuery}
      }
    }
  }

`;

export const countUnread = gql`
  query countUnread {
    countUnread {
      messages
    }
  }
`;

export const loadParticipatedAgentsQuery: TypedDocumentNode<ParticipatedAgentsResponse> = gql`
  query loadParticipatedAgents($conversationId: ID) {
    loadParticipatedAgents(conversationId: $conversationId) {
      id
      name
      firstName
      userPermissionRoleName
      phone
      email
      portrait {
        title
        type
        url
        identifier
        name
        extension
        encrypted
        index
      }
    }
  }
`;

export const templateQueryFields = `
  id
  title
  content
  attachments {
    title
    type
    url
    identifier
    name
    extension
    encrypted
    index
  }
`;

export const loadTemplateQuery: TypedDocumentNode<LoadTemplateResponse> = gql`
  query loadTemplate($page: PaginationInput) {
    loadTemplate(page: $page) {
      nodes {
        ${templateQueryFields}
      },
      page {
        ${paginationQuery}
      }
    }
  }

`;

export const loadParsedTemplatesQuery = gql`
  query loadParsedTemplates($input: LoadParsedTemplateInput) {
    loadParsedTemplates(input: $input) {
      nodes {
        ${templateQueryFields}
      },
      page {
        ${paginationQuery}
      }
    }
  }

`;

export const createTemplateMutation: TypedDocumentNode<CreateTemplateResponse> = gql`
  mutation createTemplate($template: MessageTemplateInput) {
    createTemplate(template: $template) {
      id
    }
  }
`;

export const updateTemplateMutation: TypedDocumentNode<UpdateTemplateResponse> = gql`
  mutation updateTemplate($template: MessageTemplateInput) {
    updateTemplate(template: $template)
  }
`;

export const deleteTemplateMutation = gql`
  mutation deleteTemplate($templateId: ID) {
    deleteTemplate(templateId: $templateId)
  }
`;

export const markConversationAsUnreadMutation = gql`
  mutation markConversationAsUnread($id: ID!) {
    markConversationAsUnread(id: $id) {
      status
    }
  }
`;

export const archiveConversationMutation = gql`
  mutation archiveConversation($conversationId: ID!) {
    archiveConversation(conversationId: $conversationId) {
      status
      statusText
    }
  }
`;

export const unarchiveConversationMutation = gql`
  mutation unarchiveConversation($conversationId: ID!) {
    unarchiveConversation(conversationId: $conversationId) {
      status
      statusText
    }
  }
`;
